'use client';

import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRouter } from 'next-nprogress-bar';
import type { CSSProperties } from 'react';
import { forwardRef, useId, useRef } from 'react';
import { Navigation, Scrollbar } from 'swiper/modules';
import type { SwiperClass, SwiperRef } from 'swiper/react';
import { Swiper as DefaultSwiper, SwiperSlide } from 'swiper/react';

import { useBreakpoints } from '@/hooks/use-breakpoints';
import { cn } from '@/utils';

import type { SwiperProps } from './swiper.interface';

const btnBaseClassName = 'size-8 absolute top-1/2 z-10 -translate-y-1/2 rounded-full bg-red/90 px-1 disabled:hidden';

export const Swiper = forwardRef<SwiperRef, SwiperProps>(({ items, className, containerClassName, btnLeftClassName, btnRightClassName, slideClassName, enableScrollbar = true, style, ...props }, ref) => {
  const componentId = useId().replaceAll(':', '');

  const prevButton = useRef<HTMLButtonElement>(null);
  const nextButton = useRef<HTMLButtonElement>(null);
  const swiperInstanceRef = useRef<SwiperClass | null>(null);

  const isMobile = useBreakpoints('md');
  const router = useRouter();

  const navigateToActiveSlide = (instance: SwiperClass) => {
    if (!isMobile) {
      return;
    }
    const activeSlide = instance.slides[instance.activeIndex] as HTMLElement | undefined;
    const link = activeSlide?.querySelector('a[data-state]') as HTMLAnchorElement | null;
    const href = link?.getAttribute('href');
    if (href && link?.getAttribute('data-state') !== 'active') {
      router.push(href);
    }
  };

  const handleSlideChange = (instance: SwiperClass) => {
    navigateToActiveSlide(instance);
    if (typeof props.onSlideChange === 'function') {
      props.onSlideChange(instance);
    }
  };

  /* const handleButtons = (swiper: SwiperClass) => {
    if (swiper.isEnd) {
      nextButton.current?.classList.add('hidden');
    } else {
      nextButton.current?.classList.remove('hidden');
    }

    if (swiper.isBeginning) {
      prevButton.current?.classList.add('hidden');
    } else {
      prevButton.current?.classList.remove('hidden');
    }
  }; */

  return (
    <div id={componentId} className={cn('relative', containerClassName)}>
      <button
        type="button"
        ref={prevButton}
        className={cn(
          'swiper-prev left-0',
          btnBaseClassName,
          btnLeftClassName,
        )}
      >
        <ChevronLeft className="stroke-white" />
      </button>

      <DefaultSwiper
        ref={ref}
        slidesPerView={1}
        spaceBetween={20}
        onSwiper={(instance) => {
          swiperInstanceRef.current = instance;
          if (typeof props.onSwiper === 'function') {
            props.onSwiper(instance);
          }
        }}
        {...(enableScrollbar
          ? {
              scrollbar: { draggable: true },
            }
          : {})}
        navigation={{
          nextEl: `#${componentId} .swiper-next`,
          prevEl: `#${componentId} .swiper-prev`,
        }}
        modules={enableScrollbar ? [Scrollbar, Navigation] : [Navigation]}
        className={cn(
          '[&>.swiper-wrapper]:mb-6 [&_.swiper-scrollbar-drag]:shadow-[0_0_0_2px_#E4003A] [&_.swiper-scrollbar]:mb-1',
          className,
        )}
        // onSlideChange={handleButtons}
        // onInit={handleButtons}
        style={
          {
            '--swiper-scrollbar-bg-color': '#ECECEC',
            '--swiper-scrollbar-drag-bg-color': '#E4003A',
            '--swiper-scrollbar-bottom': 0,
            '--swiper-scrollbar-size': '2px',
            ...style,
          } as CSSProperties
        }
        noSwipingClass="swiper__no-swipe"
        {...props}
        onSlideChange={handleSlideChange}
      >
        {items.map((item, index) => (
          <SwiperSlide key={index} className={slideClassName}>
            {item}
          </SwiperSlide>
        ))}
      </DefaultSwiper>

      <button
        type="button"
        ref={nextButton}
        className={cn(
          'swiper-next right-0',
          btnBaseClassName,
          btnRightClassName,
        )}
      >
        <ChevronRight className="stroke-white" />
      </button>
    </div>
  );
});
